import { createFileRoute } from "@tanstack/react-router"
import { IconFileText, IconFlag, IconArrowRight, IconAlertTriangle, IconEyeOff } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import {
  Card,
  Button,
  Text,
  Center,
  Box,
  useMantineColorScheme,
  ActionIcon,
  Title,
  Stack,
  Grid,
  Container,
  Group,
  List,
  Badge,
} from "@mantine/core"
import { SEO } from "@/components/seo/SEO"
import { ShareButtons } from "@/components/ui/ShareButtons"
import { StructuredData, createFAQPageSchema, type FAQ } from "@/components/seo/StructuredData"
import { FAQSection } from "@/components/ui/FAQSection"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/conservative")({
  component: ConservativeLandingPage,
})

const conservativeFAQs: FAQ[] = [
  {
    question: "How does government transparency relate to the Epstein case?",
    answer:
      "Recent DOJ releases contain 97% previously public material—this is selective transparency designed to deceive the American people. True transparency means releasing ALL documents, unredacted, without political filtering. The Epstein case involves powerful elites, and the government has a history of protecting its own. We demand complete disclosure to expose the deep state corruption and hold ALL individuals accountable, regardless of their connections or status.",
  },
  {
    question: "Why should conservatives care about elite accountability?",
    answer:
      "Conservatives believe in the Constitution, rule of law, and equal justice for all—not a two-tiered system that protects the powerful while prosecuting ordinary citizens. The Epstein case exposes how the elite manipulate the justice system to escape consequences. Demanding full transparency isn't partisan—it's about draining the swamp and ensuring NO ONE is above the law. If we tolerate selective justice for elites, we abandon our constitutional principles.",
  },
  {
    question: "Is this a partisan issue?",
    answer:
      "No. While the current administration is blocking transparency, this case involves individuals across the political spectrum. We demand accountability for EVERYONE implicated, regardless of party affiliation. Conservative principles require equal treatment under the law and rejection of cronyism. The swamp protects its own—Republican or Democrat—and we must demand transparency to expose ALL corruption, not just politically convenient targets.",
  },
  {
    question: "What can I do to demand transparency?",
    answer:
      "You can fight government corruption by: (1) Contacting your representatives and demanding complete, unredacted file releases, (2) Sharing verified information to combat government propaganda, (3) Supporting legislation that mandates transparency in high-profile cases, (4) Holding elected officials accountable through your vote, and (5) Rejecting partisan spin—demand the truth regardless of who it implicates. Use the action page to contact your representatives directly.",
  },
  {
    question: "How does this relate to 'draining the swamp'?",
    answer:
      "The Epstein case is a perfect example of swamp behavior: powerful elites using their connections to evade justice while the government covers for them with selective releases and convenient 'investigations.' Draining the swamp means exposing this corruption and demanding accountability from ALL powerful individuals and institutions. The 97% recycled content in recent releases shows the swamp is still protecting its own. We must demand complete transparency to truly drain the swamp.",
  },
]

function ConservativeLandingPage() {
  const { colorScheme } = useMantineColorScheme()

  return (
    <>
      <SEO
        title="Drain the Swamp - Demand Full Disclosure"
        description="Expose government corruption and elite accountability evasion. Demand complete transparency on the Epstein files—no selective releases, no political manipulation."
        canonical="https://rtefn.com/conservative"
        keywords="government transparency, drain the swamp, elite accountability, constitutional rights, DOJ corruption, FBI manipulation, deep state, political corruption, two-tiered justice, conservative values, transparency in government"
        ogType="website"
        ogImage="https://rtefn.com/og-conservative.png"
        ogImageAlt="Drain the Swamp - Release The Epstein Files Now"
      />
      <StructuredData data={createFAQPageSchema(conservativeFAQs)} />
      <Box className="animate-fade-in">
        {/* Hero Section */}
        <Box
          className={`min-h-screen flex items-center justify-center relative overflow-hidden py-16 ${
            colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-red-50 via-white to-blue-50"
          }`}
        >
          {colorScheme === "dark" && <Box className="noise-overlay" />}

          <Container size="xl" className="relative z-10">
            <Stack ta="center" maw={1280} mx="auto">
              {/* Tagline */}
              <Box className="animate-fade-in-up" style={{ animationDelay: "0.2s", marginBottom: "var(--space-3xl)" }}>
                <Box
                  component="span"
                  className="inline-block backdrop-blur-md text-lg font-medium rounded-full"
                  style={{
                    letterSpacing: "0.025em",
                    padding: "12px 28px",
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(220, 38, 38, 0.08)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(220, 38, 38, 0.2)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(153, 27, 27, 1)",
                  }}
                >
                  No More Cover-Ups • No More Corruption • No More Lies
                </Box>
              </Box>

              {/* Main Headline */}
              <h1
                className="animate-fade-in-up"
                style={{
                  animationDelay: "0.4s",
                  fontFamily: "var(--font-family)",
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 0.95,
                  fontSize: "clamp(3rem, 8vw, 7.5rem)",
                  margin: "0 0 var(--space-2xl) 0",
                  color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                }}
              >
                Drain the Swamp
                <br />
                <span className="gradient-text">Demand Full Disclosure</span>
              </h1>

              {/* Subtitle */}
              <div className="animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
                <p
                  className="max-w-4xl mx-auto"
                  style={{
                    fontFamily: "var(--font-family)",
                    fontWeight: 400,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.5,
                    fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#6b7280",
                    margin: "0 0 var(--space-3xl) 0",
                  }}
                >
                  The Deep State is hiding the truth. Demand complete transparency and accountability—for ALL elites,
                  regardless of party.
                </p>
              </div>

              {/* CTA Buttons */}
              <Box className="animate-fade-in-up" mt="xl" mb="3xl" style={{ animationDelay: "0.8s" }}>
                <PageCTASection
                  contextualAction={{ href: "/documents", label: "See The Evidence", icon: IconFileText }}
                />
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* Government Corruption Exposed */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0a0a0a 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #fee2e2 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Box maw={1152} mx="auto">
              <Card
                shadow="lg"
                radius="xl"
                p="3xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(220, 38, 38, 0.2)",
                }}
              >
                <Stack gap="xl">
                  <Group justify="center" gap="md" align="center">
                    <ActionIcon
                      variant="subtle"
                      size={64}
                      radius="md"
                      style={{ color: colorScheme === "dark" ? "#ef4444" : "#dc2626" }}
                    >
                      <IconAlertTriangle size={40} />
                    </ActionIcon>
                    <Title order={2} size="3rem" fw={700}>
                      The <span style={{ color: colorScheme === "dark" ? "#ef4444" : "#dc2626" }}>Cover-Up</span>
                    </Title>
                  </Group>

                  <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center">
                    The political establishment—<strong>both parties</strong>—has protected powerful elites for decades.
                    The Epstein case exposes how wealth and influence shield criminals from accountability. Recent DOJ
                    "document releases" are <strong>97% previously public material</strong>—a deliberate deception to
                    claim transparency while hiding the truth. The Trump administration calls genuine transparency
                    efforts "hostile acts" while releasing recycled documents. <strong>We will not be fooled.</strong>
                  </Text>

                  <Box mt="lg">
                    <Stack gap="md">
                      <Badge
                        size="xl"
                        variant="filled"
                        color="red"
                        style={{
                          alignSelf: "center",
                          padding: "20px 32px",
                          fontSize: "1.125rem",
                          fontWeight: 600,
                        }}
                      >
                        DEMAND COMPLETE, UNREDACTED TRANSPARENCY
                      </Badge>
                      <List spacing="md" size="lg" mt="md">
                        <List.Item>
                          <strong>No Selective Releases:</strong> Release ALL documents—not cherry-picked files designed
                          to protect the powerful
                        </List.Item>
                        <List.Item>
                          <strong>No Government Manipulation:</strong> Stop FBI/DOJ interference and selective editing
                        </List.Item>
                        <List.Item>
                          <strong>No Political Protection:</strong> Every name, every connection, every crime—public and
                          prosecuted
                        </List.Item>
                        <List.Item>
                          <strong>Equal Justice:</strong> The same laws must apply to billionaires, politicians, and
                          presidents as to ordinary Americans
                        </List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Why Conservatives Must Care */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0f0f23 0%, #1a1a2e 100%)"
                : "linear-gradient(180deg, #fee2e2 0%, #fecaca 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Stack ta="center" mb="4xl">
              <Title order={2} size="3rem" fw={700} mb="xl" ta="center">
                Why This <span style={{ color: colorScheme === "dark" ? "#ef4444" : "#dc2626" }}>Matters</span>
              </Title>
              <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center" c="dimmed" mb="xl">
                Limited government means accountability for ALL—especially the powerful elite
              </Text>
            </Stack>

            <Grid gutter="xl">
              {/* Two-Tiered Justice */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(220, 38, 38, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#dc2626" : "#ef4444",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconFlag size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Two-Tiered Justice
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      The elite get sweetheart deals while ordinary Americans face the full force of the law. This case
                      proves the system is rigged. We must demand equal justice under law—no exceptions for the rich and
                      connected.
                    </Text>

                    <Button
                      component={Link}
                      to="/documents"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(220, 38, 38, 0.15)" : "rgba(220, 38, 38, 0.08)",
                        color: colorScheme === "dark" ? "#f87171" : "#dc2626",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(220, 38, 38, 0.3)" : "rgba(220, 38, 38, 0.2)"}`,
                      }}
                    >
                      See The Proof
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Government Overreach */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(220, 38, 38, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#dc2626" : "#ef4444",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconEyeOff size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Deep State Deception
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      The FBI and DOJ are actively suppressing evidence. They claim "no client list exists" despite
                      witnesses and evidence suggesting otherwise. Government agencies serve the powerful, not the
                      people.
                    </Text>

                    <Button
                      component={Link}
                      to="/timeline"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(220, 38, 38, 0.15)" : "rgba(220, 38, 38, 0.08)",
                        color: colorScheme === "dark" ? "#f87171" : "#dc2626",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(220, 38, 38, 0.3)" : "rgba(220, 38, 38, 0.2)"}`,
                      }}
                    >
                      Track The Cover-Up
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Protecting Future Generations */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(220, 38, 38, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#dc2626" : "#ef4444",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconFileText size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Constitutional Rights
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      The First Amendment guarantees our right to information. Government transparency is not
                      optional—it is a constitutional requirement. We the People have a right to know what our
                      government knows.
                    </Text>

                    <Button
                      component={Link}
                      to="/news"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(220, 38, 38, 0.15)" : "rgba(220, 38, 38, 0.08)",
                        color: colorScheme === "dark" ? "#f87171" : "#dc2626",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(220, 38, 38, 0.3)" : "rgba(220, 38, 38, 0.2)"}`,
                      }}
                    >
                      Stay Informed
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* FAQ Section */}
        <FAQSection
          faqs={conservativeFAQs}
          accentColor={colorScheme === "dark" ? "#f87171" : "#dc2626"}
          title="Frequently Asked Questions"
          subtitle="Answers to common questions about government transparency and accountability"
        />

        {/* Call to Action */}
        <Box
          ta="center"
          style={{
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
            backgroundColor: colorScheme === "dark" ? "#030712" : "#7f1d1d",
          }}
        >
          <Container size="xl">
            <Stack maw={896} mx="auto" ta="center">
              <Title order={2} size="3rem" fw={600} mb="md" c="white">
                Hold ALL Elites Accountable
              </Title>

              <Text size="xl" c="gray.3" mb="xl" maw={768} mx="auto" fw={300} lh={1.6}>
                This is not about Republicans vs. Democrats. This is about We the People vs. a corrupt elite that
                believes they are above the law. It's time to drain the swamp—for real.
              </Text>

              <Stack hiddenFrom="sm" gap="lg">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Demand Transparency
                </Link>
              </Stack>
              <Group visibleFrom="sm" gap="lg" justify="center">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Demand Transparency
                </Link>
              </Group>

              {/* Share Section */}
              <Box mt="2xl">
                <Text size="sm" c="gray.4" mb="sm" ta="center" fw={500}>
                  Spread the word—don't let them silence us
                </Text>
                <Group justify="center">
                  <ShareButtons size="lg" variant="filled" />
                </Group>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  )
}
